import { useRef, useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ═══════════════════════════════════════════════════════════════════════════
   OPTRENEX — Terminal-industries-style immersive rebuild (React conversion)
   Techniques: Lenis smooth scroll + GSAP ScrollTrigger scrub,
   scroll-scrubbed procedural canvas "video sequence" hero,
   pinned feature steps w/ text-fill, counters, notch sections.
   ═══════════════════════════════════════════════════════════════════════════ */

// ═══════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════
const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const sstep = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
const CYAN = '#56cdec', CYAN_D = '#1a92b4', INK = '#e9f1f6';

// Seeded RNG
function mulberry(a) {
  return function() {
    a |= 0; a = a + 0x6D2B79F5 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
const rng = mulberry(20260706);

// Canvas fit helper
function fit(cv) {
  const dpr = Math.min(devicePixelRatio || 1, 2);
  const w = cv.clientWidth, h = cv.clientHeight;
  if (cv.width !== w * dpr || cv.height !== h * dpr) {
    cv.width = w * dpr;
    cv.height = h * dpr;
  }
  return { ctx: cv.getContext('2d'), W: cv.width, H: cv.height, dpr };
}

// ═══════════════════════════════════════════
// WORLD NETWORK DATA (shared by hero + benefit)
// ═══════════════════════════════════════════
const CLUSTERS = [
  [-0.55, -0.20, 0.15, 0.10, 26], [-0.60, -0.05, 0.07, 0.06, 10],  // N.America
  [-0.37, 0.16, 0.07, 0.13, 16],                                     // S.America
  [-0.04, -0.27, 0.08, 0.06, 20], [0.05, -0.31, 0.06, 0.04, 10],   // Europe
  [0.03, 0.03, 0.09, 0.13, 22],                                      // Africa
  [0.30, -0.26, 0.21, 0.08, 26],                                     // Russia/C.Asia
  [0.22, -0.01, 0.05, 0.07, 12],                                     // India
  [0.38, 0.09, 0.08, 0.06, 12],                                      // SEA
  [0.49, 0.27, 0.06, 0.04, 8],                                       // AUS
  [0.47, -0.16, 0.04, 0.05, 8]                                       // JP
];

const mapDots = [];
CLUSTERS.forEach(([cx, cy, rx, ry, n]) => {
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2, r = Math.sqrt(rng());
    mapDots.push({ x: cx + Math.cos(a) * rx * r, y: cy + Math.sin(a) * ry * r, tw: rng() * Math.PI * 2 });
  }
});

const HUBS = [
  { n: 'DUBAI HQ', x: 0.155, y: -0.055, main: true },
  { n: 'MUMBAI', x: 0.225, y: 0.005, src: true },
  { n: 'LONDON', x: -0.065, y: -0.305 },
  { n: 'FRANKFURT', x: 0.0, y: -0.275 },
  { n: 'NEW YORK', x: -0.50, y: -0.21 },
  { n: 'LOS ANGELES', x: -0.66, y: -0.17 },
  { n: 'RIYADH', x: 0.10, y: -0.03 },
  { n: 'SINGAPORE', x: 0.385, y: 0.075 },
  { n: 'TOKYO', x: 0.49, y: -0.175 },
  { n: 'JOHANNESBURG', x: 0.06, y: 0.26 },
  { n: 'SÃO PAULO', x: -0.345, y: 0.21 }
];

const DXB = HUBS[0];
const ARCS = HUBS.slice(1).map((h, i) => ({
  a: DXB, b: h, ph: rng(), lift: 0.10 + rng() * 0.12, dir: i % 2 ? 1 : -1
}));

function qpt(ax, ay, cx, cy, bx, by, t) {
  const u = 1 - t;
  return [u * u * ax + 2 * u * t * cx + t * t * bx, u * u * ay + 2 * u * t * cy + t * t * by];
}

function arcGeom(arc, S, ox, oy) {
  const ax = ox + arc.a.x * S, ay = oy + arc.a.y * S;
  const bx = ox + arc.b.x * S, by = oy + arc.b.y * S;
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const dx = bx - ax, dy = by - ay, L = Math.hypot(dx, dy) || 1;
  const cx = mx + (-dy / L) * arc.lift * S * arc.dir;
  const cy = my + (dx / L) * arc.lift * S * arc.dir;
  return { ax, ay, bx, by, cx, cy };
}

function drawNetwork(ctx, W, H, t, S, ox, oy, alpha, arcProg) {
  ctx.save();
  ctx.globalAlpha = alpha;
  
  // dots
  mapDots.forEach(d => {
    const tw = 0.55 + 0.45 * Math.sin(t * 1.4 + d.tw);
    ctx.fillStyle = `rgba(233,241,246,${0.34 * tw})`;
    ctx.beginPath();
    ctx.arc(ox + d.x * S, oy + d.y * S, Math.max(1, S * 0.0035), 0, 7);
    ctx.fill();
  });
  
  // arcs
  ARCS.forEach((arc, i) => {
    const g = arcGeom(arc, S, ox, oy);
    const pr = clamp(arcProg * 1.5 - i * 0.045, 0, 1);
    if (pr <= 0) return;
    
    ctx.strokeStyle = 'rgba(86,205,236,.34)';
    ctx.lineWidth = Math.max(1, S * 0.0022);
    ctx.shadowColor = CYAN;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.moveTo(g.ax, g.ay);
    
    const steps = 26;
    for (let s = 1; s <= steps * pr; s++) {
      const [px, py] = qpt(g.ax, g.ay, g.cx, g.cy, g.bx, g.by, s / steps);
      ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
    
    if (pr > 0.98) {
      const k = (t * 0.14 + arc.ph) % 1;
      const [px, py] = qpt(g.ax, g.ay, g.cx, g.cy, g.bx, g.by, k);
      ctx.fillStyle = CYAN;
      ctx.shadowColor = CYAN;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(px, py, Math.max(1.5, S * 0.004), 0, 7);
      ctx.fill();
      ctx.shadowBlur = 0;
    }
  });
  
  // hubs
  ctx.font = `600 ${Math.max(9, S * 0.016)}px "JetBrains Mono",monospace`;
  HUBS.forEach(h => {
    const x = ox + h.x * S, y = oy + h.y * S;
    const r = h.main ? S * 0.009 : S * 0.0055;
    ctx.fillStyle = h.main ? CYAN : (h.src ? '#9be8ff' : 'rgba(233,241,246,.9)');
    ctx.shadowColor = CYAN;
    ctx.shadowBlur = h.main ? 18 : 8;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(2, r), 0, 7);
    ctx.fill();
    ctx.shadowBlur = 0;
    
    if (h.main) {
      const pr2 = (t * 0.5) % 1;
      ctx.strokeStyle = `rgba(86,205,236,${0.5 * (1 - pr2)})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(x, y, r + pr2 * S * 0.045, 0, 7);
      ctx.stroke();
    }
    ctx.fillStyle = h.main ? '#bfe9f5' : 'rgba(233,241,246,.55)';
    ctx.fillText(h.n, x + S * 0.014, y + S * 0.004);
  });
  ctx.restore();
}

// ═══════════════════════════════════════════
// CONTROLLER GEOMETRY (for hero morph)
// ═══════════════════════════════════════════
function controllerPoints() {
  const right = [
    [0.00, -0.310], [0.14, -0.330], [0.30, -0.318], [0.45, -0.286],
    [0.575, -0.220], [0.665, -0.120], [0.725, 0.005], [0.760, 0.150],
    [0.762, 0.300], [0.720, 0.420], [0.630, 0.478], [0.525, 0.452],
    [0.452, 0.352], [0.402, 0.235], [0.320, 0.152], [0.200, 0.112], [0.080, 0.104]
  ];
  const outline = [...right, ...right.slice(0, -1).reverse().map(([x, y]) => [-x, y])];
  const pts = [];
  const per = 3;
  
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i], b = outline[(i + 1) % outline.length];
    for (let k = 0; k < per; k++) {
      pts.push({ x: lerp(a[0], b[0], k / per), y: lerp(a[1], b[1], k / per), g: 'body' });
    }
  }
  
  const circle = (cx, cy, r, n, g) => {
    for (let i = 0; i < n; i++) {
      const a = i / n * Math.PI * 2;
      pts.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, g });
    }
  };
  
  circle(-0.30, -0.056, 0.105, 22, 'stickL');
  circle(0.165, 0.108, 0.095, 20, 'stickR');
  circle(-0.30, -0.056, 0.048, 12, 'stickL2');
  circle(0.165, 0.108, 0.042, 10, 'stickR2');
  
  // dpad
  const dc = [-0.135, 0.108];
  const dseg = [[[-0.10, 0], [-0.03, 0]], [[0.03, 0], [0.10, 0]], [[0, -0.10], [0, -0.03]], [[0, 0.03], [0, 0.10]]];
  dseg.forEach(([a, b]) => {
    for (let k = 0; k <= 4; k++) {
      pts.push({ x: dc[0] + lerp(a[0], b[0], k / 4) * 0.9, y: dc[1] + lerp(a[1], b[1], k / 4) * 0.9, g: 'dpad' });
    }
  });
  
  // ABXY diamond
  const bx = 0.315, by = -0.062, br = 0.075;
  [[0, -br], [br, 0], [0, br], [-br, 0]].forEach(([dx, dy]) => circle(bx + dx, by + dy, 0.026, 8, 'btn'));
  circle(0, -0.10, 0.05, 14, 'home');
  
  return pts;
}

const ctrlPts = controllerPoints().map(p => ({
  ...p,
  tgt: mapDots[Math.floor(rng() * mapDots.length)],
  d: rng() * 0.4
}));

// ═══════════════════════════════════════════
// STEP SCENE RENDERER
// ═══════════════════════════════════════════
const CAPS = [
  ['Module 01', 'Wholesale'],
  ['Module 02', 'Gift cards'],
  ['Module 03', 'API'],
  ['Module 04', 'White label'],
  ['Module 05', 'Storage'],
  ['Module 06', 'Marketplace']
];

function scenePrims(ctx, W, H) {
  return {
    frame() {
      ctx.strokeStyle = 'rgba(86,205,236,.07)';
      ctx.lineWidth = 1;
      for (let x = 0; x < W; x += W / 12) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = 0; y < H; y += H / 12) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }
    }
  };
}

function drawScene(ctx, W, H, t, idx, alpha) {
  ctx.save();
  ctx.globalAlpha = alpha;
  const P = scenePrims(ctx, W, H);
  P.frame();
  const m = Math.min(W, H);
  
  if (idx === 0) {
    // wholesale: iso crate grid
    const cols = 5, rows = 4, bw = W / 8, bh = bw * 0.62;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c, lift = Math.sin(t * 1.3 + i * 0.7) * 6;
        const x = W * 0.14 + c * bw * 1.18, y = H * 0.20 + r * bh * 1.5 + lift;
        ctx.strokeStyle = `rgba(86,205,236,${0.35 + 0.3 * Math.sin(t + i)})`;
        ctx.lineWidth = 1.4;
        ctx.strokeRect(x, y, bw, bh);
        ctx.strokeStyle = 'rgba(233,241,246,.25)';
        ctx.beginPath(); ctx.moveTo(x, y + bh * 0.35); ctx.lineTo(x + bw, y + bh * 0.35); ctx.stroke();
        if ((i + Math.floor(t * 0.8)) % 7 === 0) {
          ctx.fillStyle = 'rgba(86,205,236,.14)';
          ctx.fillRect(x, y, bw, bh);
        }
      }
    }
  } else if (idx === 1) {
    // gift cards
    const cols = 4, rows = 3, cw = W / 6.2, ch = cw * 0.62;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c, x = W * 0.12 + c * cw * 1.22, y = H * 0.20 + r * ch * 1.45;
        const hot = (Math.floor(t * 1.2)) % 12 === i;
        ctx.fillStyle = hot ? 'rgba(86,205,236,.22)' : 'rgba(233,241,246,.045)';
        ctx.strokeStyle = hot ? CYAN : 'rgba(233,241,246,.25)';
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.roundRect(x, y, cw, ch, 8);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = hot ? CYAN : 'rgba(233,241,246,.4)';
        for (let b = 0; b < 6; b++) ctx.fillRect(x + 10 + b * 6, y + ch - 14, 3, 8);
        ctx.beginPath(); ctx.arc(x + cw - 14, y + 13, 4, 0, 7); ctx.fill();
      }
    }
  } else if (idx === 2) {
    // API mesh
    const n = 9, cxx = W / 2, cyy = H / 2, R = m * 0.30;
    const nodes = [[cxx, cyy]];
    for (let i = 0; i < n; i++) {
      const a = i / n * Math.PI * 2 + t * 0.12;
      nodes.push([cxx + Math.cos(a) * R, cyy + Math.sin(a) * R * 0.8]);
    }
    ctx.strokeStyle = 'rgba(86,205,236,.3)';
    ctx.lineWidth = 1;
    for (let i = 1; i < nodes.length; i++) {
      ctx.beginPath(); ctx.moveTo(nodes[0][0], nodes[0][1]); ctx.lineTo(nodes[i][0], nodes[i][1]); ctx.stroke();
      const k = (t * 0.35 + i * 0.13) % 1;
      ctx.fillStyle = CYAN;
      ctx.beginPath();
      ctx.arc(lerp(nodes[0][0], nodes[i][0], k), lerp(nodes[0][1], nodes[i][1], k), 2.4, 0, 7);
      ctx.fill();
    }
    nodes.forEach(([x, y], i) => {
      ctx.fillStyle = i ? 'rgba(233,241,246,.85)' : CYAN;
      ctx.shadowColor = CYAN;
      ctx.shadowBlur = i ? 0 : 16;
      ctx.beginPath(); ctx.arc(x, y, i ? 3 : 7, 0, 7); ctx.fill();
      ctx.shadowBlur = 0;
    });
    ctx.font = `600 ${m * 0.03}px "JetBrains Mono",monospace`;
    ctx.fillStyle = 'rgba(191,233,245,.7)';
    ctx.fillText('GET /v1/skus  ·  15,000+', W * 0.10, H * 0.09);
  } else if (idx === 3) {
    // white label branch
    const x0 = W * 0.14, y0 = H / 2, x1 = W * 0.86;
    const outs = [H * 0.24, H * 0.5, H * 0.76];
    outs.forEach((yy, i) => {
      ctx.strokeStyle = 'rgba(86,205,236,.35)';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.bezierCurveTo(W * 0.45, y0, W * 0.5, yy, x1, yy);
      ctx.stroke();
      const k = (t * 0.3 + i * 0.33) % 1;
      const bx2 = x0 + (x1 - x0) * k, by2 = y0 + (yy - y0) * sstep(k);
      ctx.fillStyle = '#9be8ff';
      ctx.shadowColor = CYAN;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.roundRect(bx2 - 7, by2 - 5, 14, 10, 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(233,241,246,.5)';
      ctx.strokeRect(x1 - 16, yy - 16, 32, 32);
      ctx.fillStyle = 'rgba(86,205,236,.8)';
      ctx.font = `600 ${m * 0.026}px "JetBrains Mono",monospace`;
      ctx.fillText('YOUR BRAND', x1 - m * 0.14, yy + m * 0.085);
    });
    ctx.fillStyle = CYAN;
    ctx.shadowColor = CYAN;
    ctx.shadowBlur = 14;
    ctx.beginPath(); ctx.arc(x0, y0, 9, 0, 7); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(191,233,245,.7)';
    ctx.font = `600 ${m * 0.026}px "JetBrains Mono",monospace`;
    ctx.fillText('OPX ENGINE', x0 - m * 0.03, y0 + m * 0.09);
  } else if (idx === 4) {
    // storage racks + scan
    const cols = 6, rows = 5, cw = W / 9, ch = H / 9;
    const scan = (t * 0.25 % 1) * W;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = W * 0.10 + c * cw * 1.28, y = H * 0.14 + r * ch * 1.32;
        const lit = Math.abs(x - scan) < cw;
        ctx.strokeStyle = lit ? CYAN : 'rgba(233,241,246,.22)';
        ctx.lineWidth = lit ? 1.6 : 1;
        ctx.strokeRect(x, y, cw, ch);
        if ((r * cols + c) % 4 === 1) {
          ctx.fillStyle = lit ? 'rgba(86,205,236,.25)' : 'rgba(233,241,246,.06)';
          ctx.fillRect(x + 3, y + 3, cw - 6, ch - 6);
        }
      }
    }
    ctx.fillStyle = 'rgba(86,205,236,.10)';
    ctx.fillRect(scan - 8, 0, 16, H);
  } else {
    // marketplace bars
    const n = 8, bw = W / (n * 1.9);
    for (let i = 0; i < n; i++) {
      const h2 = (0.25 + 0.65 * Math.abs(Math.sin(t * 0.7 + i * 0.9))) * H * 0.55;
      const x = W * 0.10 + i * bw * 1.7, y = H * 0.82 - h2;
      const g = ctx.createLinearGradient(0, y, 0, H * 0.82);
      g.addColorStop(0, 'rgba(86,205,236,.85)');
      g.addColorStop(1, 'rgba(86,205,236,.05)');
      ctx.fillStyle = g;
      ctx.fillRect(x, y, bw, h2);
      ctx.strokeStyle = 'rgba(233,241,246,.25)';
      ctx.strokeRect(x, y, bw, h2);
    }
    ctx.font = `600 ${m * 0.03}px "JetBrains Mono",monospace`;
    ctx.fillStyle = 'rgba(191,233,245,.75)';
    ctx.fillText('AMAZON · NOON — OFFICIAL VENDOR', W * 0.10, H * 0.10);
  }
  ctx.restore();
}

// ═══════════════════════════════════════════
// MAIN HOME COMPONENT
// ═══════════════════════════════════════════
export default function Home() {
  // Canvas refs
  const heroCanvasRef = useRef(null);
  const stepCanvasRef = useRef(null);
  const gridCanvasRef = useRef(null);
  const benefitCanvasRefs = useRef([]);
  
  // State refs for animation
  const heroState = useRef({ p: 0, tp: 0 });
  const stepState = useRef({ cur: 0, alpha: 1, prev: 0, fade: 1 });
  const visibleMap = useRef(new WeakMap());
  const animationRef = useRef(null);
  const startTimeRef = useRef(performance.now());
  
  // Counter state
  const countersTriggeredRef = useRef(false);
  const [counterValues, setCounterValues] = useState({
    imports: 0,
    exports: 0,
    giftcards: 0,
    skus: 0,
    countries: 0,
    retailers: 0
  });
  
  // Step caption state
  const [stepCaption, setStepCaption] = useState({ label: 'Module 01', title: 'Wholesale' });
  
  // Draw Hero Canvas
  const drawHero = useCallback((t) => {
    const cv = heroCanvasRef.current;
    if (!cv || visibleMap.current.get(cv) === false) return;
    
    const { ctx, W, H } = fit(cv);
    const state = heroState.current;
    state.p = lerp(state.p, state.tp, 0.14);
    const p = state.p;
    
    ctx.clearRect(0, 0, W, H);
    
    // bg gradient
    const bg = ctx.createRadialGradient(W / 2, H * 0.42, 0, W / 2, H * 0.42, Math.max(W, H) * 0.75);
    bg.addColorStop(0, '#07131b');
    bg.addColorStop(0.55, '#030a10');
    bg.addColorStop(1, '#010305');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    
    // ambient starfield
    ctx.save();
    for (let i = 0; i < 70; i++) {
      const sx = (mulberry(i * 7 + 3)() * 1.0) * W;
      const sy = (mulberry(i * 13 + 1)() * 1.0) * H;
      const a = 0.05 + 0.16 * Math.abs(Math.sin(t * 0.6 + i));
      ctx.fillStyle = `rgba(160,220,240,${a})`;
      ctx.fillRect(sx, sy, 1.4, 1.4);
    }
    ctx.restore();
    
    const A = sstep((p - 0.00) / 0.055);
    const M = sstep((p - 0.26) / 0.26);
    const N = sstep((p - 0.50) / 0.20);
    const Z = sstep((p - 0.86) / 0.14);
    const rot = Math.sin(t * 0.35) * 0.05 * (1 - M);
    const s2 = Math.min(W, H) * 0.335 * (1 + 0.14 * sstep(p / 0.26));
    const S = Math.min(W / 1.62, H / 0.98);
    const cx = W / 2, cy = H * 0.465;
    const zoom = 1 - 0.06 * Z;
    
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(zoom, zoom);
    ctx.translate(-cx, -cy);
    
    // morphing points
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    
    const ptsNow = ctrlPts.map(pt => {
      const m = sstep((M - pt.d) / 0.6);
      const hx = pt.x * s2, hy = pt.y * s2;
      const tx = pt.tgt.x * S, ty = pt.tgt.y * S;
      return { x: lerp(hx, tx, m), y: lerp(hy, ty, m), g: pt.g, m };
    });
    
    // controller edges
    const edgeA = (1 - sstep(M * 1.7)) * A;
    if (edgeA > 0.01) {
      ctx.lineWidth = Math.max(1, s2 * 0.008);
      ctx.shadowColor = CYAN;
      let prev = null;
      ptsNow.forEach((q, i) => {
        if (prev && prev.g === q.g) {
          const gl = 0.35 + 0.3 * Math.sin(t * 2 + i * 0.4);
          ctx.strokeStyle = `rgba(86,205,236,${edgeA * gl})`;
          ctx.shadowBlur = 10 * edgeA;
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(q.x, q.y);
          ctx.stroke();
        }
        prev = { ...q };
      });
      ctx.shadowBlur = 0;
    }
    
    // vertices
    ptsNow.forEach((q, i) => {
      const a = A * (1 - q.m * 0.65) * (1 - Z * 0.4);
      if (a < 0.02) return;
      const r = Math.max(1.1, s2 * 0.006 * (1 - q.m * 0.5));
      ctx.fillStyle = `rgba(190,236,250,${0.8 * a})`;
      ctx.beginPath();
      ctx.arc(q.x, q.y, r, 0, 7);
      ctx.fill();
    });
    ctx.restore();
    
    // world network fades in
    const netA = sstep((M - 0.55) / 0.45) * (1 - Z * 0.35);
    if (netA > 0.01) drawNetwork(ctx, W, H, t, S, cx, cy, netA, N);
    
    // floor grid appears late
    const gA = N * (0.5 - 0.2 * Z);
    if (gA > 0.01) {
      ctx.save();
      ctx.globalAlpha = gA * 0.35;
      ctx.strokeStyle = 'rgba(86,205,236,.25)';
      ctx.lineWidth = 1;
      const gy0 = H * 0.80;
      for (let i = 0; i < 9; i++) {
        const gy = gy0 + i * i * 2.2;
        ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke();
      }
      for (let i = -14; i <= 14; i++) {
        ctx.beginPath();
        ctx.moveTo(W / 2 + i * W * 0.045, gy0);
        ctx.lineTo(W / 2 + i * W * 0.16, H);
        ctx.stroke();
      }
      ctx.restore();
    }
    ctx.restore();
    
    // scan sweep line during morph
    if (M > 0.02 && M < 0.98) {
      const sy = H * (0.15 + 0.7 * M);
      const g2 = ctx.createLinearGradient(0, sy - 40, 0, sy + 40);
      g2.addColorStop(0, 'rgba(86,205,236,0)');
      g2.addColorStop(0.5, 'rgba(86,205,236,.10)');
      g2.addColorStop(1, 'rgba(86,205,236,0)');
      ctx.fillStyle = g2;
      ctx.fillRect(0, sy - 40, W, 80);
    }
  }, []);
  
  // Draw Steps Canvas
  const drawSteps = useCallback((t) => {
    const cv = stepCanvasRef.current;
    if (!cv || visibleMap.current.get(cv) === false) return;
    
    const { ctx, W, H } = fit(cv);
    ctx.clearRect(0, 0, W, H);
    
    const state = stepState.current;
    state.fade = Math.min(1, state.fade + 0.05);
    
    if (state.fade < 1) drawScene(ctx, W, H, t, state.prev, 1 - state.fade);
    drawScene(ctx, W, H, t, state.cur, state.fade);
  }, []);
  
  // Draw Grid Canvas (interstitial)
  const drawGrid = useCallback((t) => {
    const cv = gridCanvasRef.current;
    if (!cv || visibleMap.current.get(cv) === false) return;
    
    const { ctx, W, H } = fit(cv);
    ctx.clearRect(0, 0, W, H);
    
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#05161f');
    bg.addColorStop(1, '#031017');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    
    const cell = Math.max(46, W / 26), off = (t * 12) % cell;
    ctx.strokeStyle = 'rgba(86,205,236,.10)';
    ctx.lineWidth = 1;
    
    for (let x = -off; x < W; x += cell) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = -off; y < H; y += cell) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }
    for (let x = -off; x < W; x += cell) {
      for (let y = -off; y < H; y += cell) {
        const a = 0.10 + 0.20 * Math.abs(Math.sin(t * 0.8 + x * 0.02 + y * 0.03));
        ctx.fillStyle = `rgba(150,224,244,${a})`;
        ctx.fillRect(x - 1, y - 1, 2, 2);
      }
    }
  }, []);
  
  // Draw Benefit Canvases
  const drawBenefit = useCallback((cv, t) => {
    if (!cv || visibleMap.current.get(cv) === false) return;
    
    const { ctx, W, H } = fit(cv);
    const mode = cv.dataset.mode;
    ctx.clearRect(0, 0, W, H);
    
    const bg = ctx.createRadialGradient(W / 2, H * 0.45, 0, W / 2, H * 0.45, Math.max(W, H) * 0.8);
    bg.addColorStop(0, '#06131b');
    bg.addColorStop(1, '#010304');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    
    if (mode === 'network') {
      drawNetwork(ctx, W, H, t, Math.min(W / 1.55, H / 0.92), W / 2, H * 0.46, 1, 1);
    } else if (mode === 'speed') {
      const lanes = 7;
      for (let i = 0; i < lanes; i++) {
        const y = H * (0.18 + i * 0.62 / lanes);
        ctx.strokeStyle = 'rgba(233,241,246,.08)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
        
        const k = ((t * (0.25 + i * 0.07)) % 1.2) - 0.1;
        const x = k * W, len = W * 0.16;
        const g = ctx.createLinearGradient(x - len, 0, x, 0);
        g.addColorStop(0, 'rgba(86,205,236,0)');
        g.addColorStop(1, CYAN);
        ctx.strokeStyle = g;
        ctx.lineWidth = 2.4;
        ctx.shadowColor = CYAN;
        ctx.shadowBlur = 10;
        ctx.beginPath(); ctx.moveTo(x - len, y); ctx.lineTo(x, y); ctx.stroke();
        ctx.shadowBlur = 0;
      }
      
      const cx2 = W * 0.78, cy2 = H * 0.42, R = Math.min(W, H) * 0.16;
      ctx.strokeStyle = 'rgba(86,205,236,.3)';
      ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(cx2, cy2, R, 0, 7); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx2, cy2, R * 0.65, 0, 7); ctx.stroke();
      
      ctx.font = `600 ${Math.min(W, H) * 0.022}px "JetBrains Mono",monospace`;
      ctx.fillStyle = 'rgba(191,233,245,.8)';
      ctx.fillText('<48H TO MAJOR MARKETS', W * 0.055, H * 0.14);
    } else {
      // scale: chart
      const n = 60, base = H * 0.78, amp = H * 0.34;
      ctx.strokeStyle = 'rgba(233,241,246,.08)';
      for (let i = 1; i < 5; i++) {
        const y = base - amp * i / 4;
        ctx.beginPath(); ctx.moveTo(W * 0.05, y); ctx.lineTo(W * 0.95, y); ctx.stroke();
      }
      
      ctx.beginPath();
      for (let i = 0; i <= n; i++) {
        const x = W * 0.05 + i / n * W * 0.9;
        const v = 0.15 + 0.8 * sstep(i / n) * (0.75 + 0.25 * Math.sin(t * 0.8 + i * 0.35));
        const y = base - amp * v;
        i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
      }
      ctx.strokeStyle = CYAN;
      ctx.lineWidth = 2.2;
      ctx.shadowColor = CYAN;
      ctx.shadowBlur = 14;
      ctx.stroke();
      ctx.shadowBlur = 0;
      
      ctx.lineTo(W * 0.95, base);
      ctx.lineTo(W * 0.05, base);
      ctx.closePath();
      
      const g4 = ctx.createLinearGradient(0, base - amp, 0, base);
      g4.addColorStop(0, 'rgba(86,205,236,.22)');
      g4.addColorStop(1, 'rgba(86,205,236,0)');
      ctx.fillStyle = g4;
      ctx.fill();
      
      ctx.font = `600 ${Math.min(W, H) * 0.022}px "JetBrains Mono",monospace`;
      ctx.fillStyle = 'rgba(191,233,245,.8)';
      ctx.fillText('GROUP TURNOVER — $100M+ RUN RATE', W * 0.055, H * 0.14);
    }
  }, []);
  
  // Main animation loop
  useEffect(() => {
    const loop = (now) => {
      const t = (now - startTimeRef.current) / 1000;
      drawHero(t);
      drawSteps(t);
      drawGrid(t);
      benefitCanvasRefs.current.forEach(cv => drawBenefit(cv, t));
      animationRef.current = requestAnimationFrame(loop);
    };
    
    animationRef.current = requestAnimationFrame(loop);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [drawHero, drawSteps, drawGrid, drawBenefit]);
  
  // Setup GSAP ScrollTriggers
  useEffect(() => {
    // Intersection Observer for canvas visibility
    const io = new IntersectionObserver(
      entries => entries.forEach(e => visibleMap.current.set(e.target, e.isIntersecting)),
      { rootMargin: '12%' }
    );
    
    [heroCanvasRef.current, stepCanvasRef.current, gridCanvasRef.current, ...benefitCanvasRefs.current]
      .filter(Boolean)
      .forEach(cv => io.observe(cv));
    
    // Hero scroll trigger
    const heroTrigger = ScrollTrigger.create({
      trigger: '.hero',
      start: 'top top',
      end: 'bottom bottom',
      scrub: true,
      onUpdate: st => { heroState.current.tp = st.progress; }
    });
    
    // Hero stage crossfades
    const heroTl = gsap.timeline({
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.4
      }
    });
    
    heroTl
      .to('.hero-hint', { opacity: 0, duration: 0.04 }, 0.055)
      .to('#stage1', { opacity: 0, y: -70, scale: 0.96, duration: 0.09 }, 0.185)
      .fromTo('#stage2', { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.1 }, 0.315)
      .to('#stage2', { opacity: 0, y: -70, duration: 0.09 }, 0.560)
      .fromTo('#stage3', { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.11 }, 0.700);
    
    // Step triggers
    const stepEls = document.querySelectorAll('.step');
    stepEls.forEach(el => {
      const idx = parseInt(el.dataset.scene, 10);
      
      ScrollTrigger.create({
        trigger: el,
        start: 'top 60%',
        end: 'bottom 60%',
        onEnter: () => {
          if (idx !== stepState.current.cur) {
            stepState.current.prev = stepState.current.cur;
            stepState.current.cur = idx;
            stepState.current.fade = 0;
            setStepCaption({ label: CAPS[idx][0], title: CAPS[idx][1] });
          }
        },
        onEnterBack: () => {
          if (idx !== stepState.current.cur) {
            stepState.current.prev = stepState.current.cur;
            stepState.current.cur = idx;
            stepState.current.fade = 0;
            setStepCaption({ label: CAPS[idx][0], title: CAPS[idx][1] });
          }
        }
      });
      
      // Text fill scrub
      const stepText = el.querySelector('.step-text');
      if (stepText) {
        gsap.fromTo(stepText, 
          { backgroundPosition: '100% 0' },
          {
            backgroundPosition: '0% 0',
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top 78%',
              end: 'top 32%',
              scrub: true
            }
          }
        );
      }
    });
    
    // Interstitial word reveal
    const interH = document.getElementById('interH');
    if (interH) {
      const originalHTML = interH.innerHTML;
      interH.innerHTML = originalHTML.split(/(<[^>]+>|\s+)/g).map(tok => {
        if (!tok.trim() || tok.startsWith('<')) return tok;
        return `<span class="w"><span class="wi">${tok}</span></span>`;
      }).join(' ');
      
      gsap.to('#interH .wi', {
        y: 0,
        stagger: 0.06,
        ease: 'power3.out',
        duration: 1,
        scrollTrigger: { trigger: '.interstitial', start: 'top 55%' }
      });
      
      gsap.fromTo('.inter-copy', 
        { scale: 0.92 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: '.interstitial',
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        }
      );
    }
    
    // Reveal animations
    const rio = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          rio.unobserve(e.target);
        }
      }),
      { threshold: 0.18 }
    );
    document.querySelectorAll('[data-reveal]').forEach(el => rio.observe(el));
    
    // Counter animation
    const statTriggers = [];
    document.querySelectorAll('.stat').forEach(el => {
      const trigger = ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          el.classList.add('on');
          if (!countersTriggeredRef.current) {
            countersTriggeredRef.current = true;
            animateCounters();
          }
        }
      });
      statTriggers.push(trigger);
    });
    
    // Resize handler
    const handleResize = () => ScrollTrigger.refresh();
    window.addEventListener('resize', handleResize);
    
    return () => {
      heroTrigger.kill();
      heroTl.kill();
      ScrollTrigger.getAll().forEach(t => t.kill());
      io.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Counter animation
  const animateCounters = () => {
    const targets = {
      imports: 1000000,
      exports: 500000,
      giftcards: 2000000,
      skus: 15000,
      countries: 41,
      retailers: 203
    };
    
    const duration = 1800;
    const start = performance.now();
    
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOut
      
      setCounterValues({
        imports: Math.round(targets.imports * eased),
        exports: Math.round(targets.exports * eased),
        giftcards: Math.round(targets.giftcards * eased),
        skus: Math.round(targets.skus * eased),
        countries: Math.round(targets.countries * eased),
        retailers: Math.round(targets.retailers * eased)
      });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };
  
  // Format counter values
  const fmtVal = (v, format, suffix) => {
    if (format === 'M') {
      const m = v / 1e6;
      return (m < 1 ? m.toFixed(1) : Math.round(m)) + 'M' + suffix;
    }
    if (format === 'K') {
      return Math.round(v / 1e3) + 'K' + suffix;
    }
    return Math.round(v) + suffix;
  };
  
  // Marquee brands
  const brands = [
    'PlayStation', 'Xbox', 'Nintendo', 'Rockstar Games', 'EA Sports',
    'Steam', 'Meta Quest', 'Sega', 'Capcom', 'Ubisoft',
    'Bandai Namco', 'Take-Two', 'Square Enix', 'THQ Nordic', 'Activision'
  ];
  
  return (
    <main data-testid="home-page">
      {/* Grain overlay */}
      <div className="grain" />
      
      {/* WhatsApp Float */}
      <a
        href="https://wa.me/971556953759?text=Hello%20Optrenex%2C%20I%27m%20interested%20in%20your%20gaming%20distribution%20services."
        aria-label="WhatsApp"
        data-testid="whatsapp-btn"
        className="fixed right-[22px] bottom-[22px] z-[110] w-[52px] h-[52px] rounded-full bg-[#25D366] grid place-items-center shadow-[0_8px_30px_rgba(0,0,0,.45)] hover:scale-110 transition-transform"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff">
          <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.5 14.2c-.2.7-1.3 1.3-1.9 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-2.9-1.3-4.8-4.2-5-4.4-.1-.2-1.2-1.6-1.2-3s.7-2.1 1-2.4c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2.1c.1.2.1.4 0 .6l-.4.6-.5.6c-.1.1-.2.3-.1.5.1.2.6 1.1 1.4 1.8 1 .9 1.8 1.2 2.1 1.3.2.1.4.1.5-.1l.7-.8c.2-.2.4-.2.6-.1l2 .9c.2.1.4.2.4.3.1.1.1.7-.3 1.3Z"/>
        </svg>
      </a>
      
      {/* Call Float */}
      <a
        href="tel:+97145723353"
        aria-label="Call Optrenex"
        data-testid="call-btn"
        className="fixed right-[22px] bottom-[86px] z-[110] w-[52px] h-[52px] rounded-full bg-cyan grid place-items-center shadow-[0_8px_30px_rgba(0,0,0,.45)] hover:scale-110 transition-transform"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#03222d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"/>
        </svg>
      </a>

      
      {/* ═══════════════════════════════════════════ HERO ═══════════════════════════════════════════ */}
      <header className="hero" id="top" data-testid="hero-section">
        <div className="hero-sticky">
          <canvas id="heroCanvas" ref={heroCanvasRef} />
          <div className="hero-vignette" />
          
          {/* HUD Elements */}
          <div className="hud hud-tl">OPX-NET / LIVE<br />25.2653° N — 55.2975° E</div>
          <div className="hud hud-tr">DUBAI HQ · AL BUTEEN<br />MEDIA LIC 0559605</div>
          <div className="hud hud-bl">FEED 01 — DIGITAL TWIN</div>
          <div className="hud hud-br">41 COUNTRIES ACTIVE</div>
          
          <div className="hero-ui">
            {/* Stage 1 */}
            <div className="hero-stage" id="stage1">
              <div className="hero-badge">
                <span className="lbl"><span className="dot" />$100M+ Annual Group Turnover · Dubai UAE HQ</span>
              </div>
              <h1 className="hero-h h-display">
                We move the<br />world&apos;s <span className="acc">games.</span>
              </h1>
            </div>
            
            {/* Stage 2 */}
            <div className="hero-stage" id="stage2" style={{ opacity: 0 }}>
              <h2 className="hero-h h-display">One hub.<br /><span className="acc">41 countries.</span></h2>
              <p className="hero-sub">
                1M+ units imported. 500K+ exported. 2M+ gift cards a year — flowing from Mumbai and Dubai to every major gaming market on earth.
              </p>
            </div>
            
            {/* Stage 3 */}
            <div className="hero-stage" id="stage3" style={{ opacity: 0 }}>
              <h2 className="hero-h h-display">Global gaming<br />distribution. <span className="acc">Redefined.</span></h2>
              <p className="hero-sub">
                PS5, Xbox, Nintendo wholesale. API, dropshipping, white-label and storage — one platform for B2B gaming trade.
              </p>
              <div className="hero-ctas">
                <a className="btn btn-accent" href="https://b2b.optrenex.com/register" data-testid="hero-customer-btn">Become a customer</a>
                <a className="btn btn-line" href="https://b2b.optrenex.com/register" data-testid="hero-supplier-btn">Become a supplier</a>
                <a className="btn btn-line" href="https://retail.optrenex.com" data-testid="hero-retail-btn">Browse retail</a>
              </div>
            </div>
          </div>
          
          <div className="hero-hint">
            <span className="lbl">Scroll to explore</span>
            <span className="bar" />
          </div>
        </div>
      </header>
      
      {/* Notch separator */}
      <div className="sep">
        <svg viewBox="0 0 1440 64" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,64 L0,34 Q0,22 14,22 L560,22 Q580,22 592,10 Q600,0 616,0 L824,0 Q840,0 848,10 Q860,22 880,22 L1426,22 Q1440,22 1440,34 L1440,64 Z" fill="#eef0ec"/>
        </svg>
      </div>
      
      {/* ═══════════════════════════════════════════ LOGO WALL ═══════════════════════════════════════════ */}
      <section className="paper logos" id="portfolio" data-testid="brand-slider">
        <span className="lbl"><span className="dot" />Stocking the shelves behind the brands you know</span>
        <div className="marquee">
          <div className="marquee-track" id="mq1">
            {[...brands, ...brands].map((b, i) => (
              <span key={i} className="mq-item">{b}<span className="tag">™</span></span>
            ))}
          </div>
        </div>
        <div className="marquee">
          <div className="marquee-track" id="mq2">
            {[...brands.slice(7), ...brands.slice(0, 7), ...brands.slice(7), ...brands.slice(0, 7)].map((b, i) => (
              <span key={i} className="mq-item">{b}<span className="tag">™</span></span>
            ))}
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ INTRO STATEMENT ═══════════════════════════════════════════ */}
      <section className="paper intro">
        <div className="wrap">
          <span className="lbl" data-reveal><span className="dot" />Imagine your supply chain</span>
          <h2 data-reveal>
            Imagine one distribution engine as an <em>intelligent bridge</em> — seamlessly connecting publishers to every retail shelf and screen on earth.
          </h2>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ FEATURE STEPS ═══════════════════════════════════════════ */}
      <section className="paper steps" id="services">
        <div className="wrap steps-grid">
          <div className="steps-list" id="stepsList">
            <div className="step" data-scene="0">
              <span className="step-num">01</span>
              <div>
                <p className="step-text">Wholesale distribution of every major console &amp; title</p>
                <small>PS5 · PS4 · Xbox Series X/S · Nintendo Switch · Steam Deck · Meta Quest — 5,000+ physical SKUs at true wholesale pricing.</small>
              </div>
            </div>
            <div className="step" data-scene="1">
              <span className="step-num">02</span>
              <div>
                <p className="step-text">2M+ digital gift cards delivered every year</p>
                <small>PSN, Xbox, Steam, Google Play, Apple — India, UAE, US &amp; global denominations, delivered instantly.</small>
              </div>
            </div>
            <div className="step" data-scene="2">
              <span className="step-num">03</span>
              <div>
                <p className="step-text">15,000+ SKUs through one real-time API</p>
                <small>Live inventory, regional pricing and dynamic feeds for marketplaces and D2C storefronts.</small>
              </div>
            </div>
            <div className="step" data-scene="3">
              <span className="step-num">04</span>
              <div>
                <p className="step-text">Dropshipping &amp; white-label under your brand</p>
                <small>Discrete B2B fulfilment — your brand on every customer-facing touchpoint, our engine underneath.</small>
              </div>
            </div>
            <div className="step" data-scene="4">
              <span className="step-num">05</span>
              <div>
                <p className="step-text">Climate-controlled storage &amp; fulfilment in Dubai</p>
                <small>Dedicated warehousing in Dubai with pick-pack-ship — round-the-clock operations.</small>
              </div>
            </div>
            <div className="step" data-scene="5">
              <span className="step-num">06</span>
              <div>
                <p className="step-text">Official marketplace vendor at global scale</p>
                <small>Amazon &amp; Noon vendor accounts across multiple countries — leverage our presence for your products.</small>
              </div>
            </div>
          </div>
          
          <div className="steps-visual">
            <div className="steps-panel">
              <canvas id="stepCanvas" ref={stepCanvasRef} />
              <div className="cap">
                <span className="lbl" id="stepCapLbl">{stepCaption.label}</span>
                <b id="stepCapTitle">{stepCaption.title}</b>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ INTERSTITIAL ═══════════════════════════════════════════ */}
      <section className="interstitial">
        <div className="inter-sticky">
          <canvas id="gridCanvas" ref={gridCanvasRef} />
          <div className="inter-copy">
            <span className="lbl">That&apos;s</span>
            <h2 className="h-display" id="interH">Optrenex <span className="acc">International.</span></h2>
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ BENEFITS ═══════════════════════════════════════════ */}
      <section className="benefit" id="network">
        <div className="media">
          <canvas 
            className="bCanvas" 
            data-mode="network"
            ref={el => { if (el) benefitCanvasRefs.current[0] = el; }}
          />
          <div className="media-cap">
            <span className="lbl"><span className="dot" />Live distribution network</span>
          </div>
        </div>
        <div className="copy">
          <div>
            <span className="lbl dark-lbl">Benefit 01</span>
            <h3 data-reveal><u>Planet-wide</u> reach, one partner</h3>
          </div>
          <div>
            <p data-reveal>
              Real-time distribution across 41 countries — from Mumbai and Dubai to the UK, Europe, USA, GCC, Southeast Asia and Africa. Direct relationships with 100+ Indian retailers, 203+ global retailers and 250+ B2B companies of every size.
            </p>
            <div className="kpis" data-reveal>
              <div className="kpi"><b>41</b><span>Countries served</span></div>
              <div className="kpi"><b>203+</b><span>Global retailers</span></div>
              <div className="kpi"><b>250+</b><span>B2B companies</span></div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="benefit">
        <div className="media">
          <canvas 
            className="bCanvas" 
            data-mode="speed"
            ref={el => { if (el) benefitCanvasRefs.current[1] = el; }}
          />
          <div className="media-cap">
            <span className="lbl"><span className="dot" />Fulfilment telemetry</span>
          </div>
        </div>
        <div className="copy">
          <div>
            <span className="lbl dark-lbl">Benefit 02</span>
            <h3 data-reveal><u>Speed</u> as a standard</h3>
          </div>
          <div>
            <p data-reveal>
              Round-the-clock operations out of Dubai with under-48-hour delivery to major markets and a 99.8% on-time rate. Priority allocations on limited stock and new releases through direct brand relationships.
            </p>
            <div className="kpis" data-reveal>
              <div className="kpi"><b>&lt;48h</b><span>To major markets</span></div>
              <div className="kpi"><b>99.8%</b><span>On-time delivery</span></div>
              <div className="kpi"><b>24/7</b><span>Operations</span></div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="benefit">
        <div className="media">
          <canvas 
            className="bCanvas" 
            data-mode="scale"
            ref={el => { if (el) benefitCanvasRefs.current[2] = el; }}
          />
          <div className="media-cap">
            <span className="lbl"><span className="dot" />Group financials</span>
          </div>
        </div>
        <div className="copy">
          <div>
            <span className="lbl dark-lbl">Benefit 03</span>
            <h3 data-reveal>A <u>financial powerhouse</u> behind every order</h3>
          </div>
          <div>
            <p data-reveal>
              Backed by Advani Holdings with $100M+ annual group turnover and $10M in overseas direct investment — the stability and scale to handle the largest volume requirements globally, 100% ODI &amp; FDI compliant.
            </p>
            <div className="kpis" data-reveal>
              <div className="kpi"><b>$100M+</b><span>Group turnover</span></div>
              <div className="kpi"><b>$10M</b><span>Direct investment</span></div>
              <div className="kpi"><b>100%</b><span>ODI / FDI compliant</span></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ STATS ═══════════════════════════════════════════ */}
      <section className="paper stats">
        <div className="wrap">
          <span className="lbl" data-reveal><span className="dot" />Sales volume</span>
          <h2 data-reveal>Numbers that speak.</h2>
          <div className="stats-grid">
            <div className="stat" data-count="1000000" data-fmt="M" data-suf="+">
              <div className="stat-num">
                {fmtVal(counterValues.imports, 'M', '+').split(/([MK+])/).map((part, i) => 
                  /[MK+]/.test(part) ? <span key={i} className="u">{part}</span> : part
                )}
              </div>
              <p>Physical units imported annually</p>
            </div>
            <div className="stat" data-count="500000" data-fmt="K" data-suf="+">
              <div className="stat-num">
                {fmtVal(counterValues.exports, 'K', '+').split(/([MK+])/).map((part, i) => 
                  /[MK+]/.test(part) ? <span key={i} className="u">{part}</span> : part
                )}
              </div>
              <p>Units exported globally</p>
            </div>
            <div className="stat" data-count="2000000" data-fmt="M" data-suf="+">
              <div className="stat-num">
                {fmtVal(counterValues.giftcards, 'M', '+').split(/([MK+])/).map((part, i) => 
                  /[MK+]/.test(part) ? <span key={i} className="u">{part}</span> : part
                )}
              </div>
              <p>Gift cards sold / 12 months</p>
            </div>
            <div className="stat" data-count="15000" data-fmt="K" data-suf="+">
              <div className="stat-num">
                {fmtVal(counterValues.skus, 'K', '+').split(/([MK+])/).map((part, i) => 
                  /[MK+]/.test(part) ? <span key={i} className="u">{part}</span> : part
                )}
              </div>
              <p>API catalog SKUs</p>
            </div>
            <div className="stat" data-count="41" data-fmt="" data-suf="">
              <div className="stat-num">{counterValues.countries}</div>
              <p>Countries served</p>
            </div>
            <div className="stat" data-count="203" data-fmt="" data-suf="+">
              <div className="stat-num">
                {counterValues.retailers}<span className="u">+</span>
              </div>
              <p>Global retailers</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ TRUST ═══════════════════════════════════════════ */}
      <section className="paper trust" id="credentials">
        <div className="wrap">
          <div className="trust-card">
            <span className="lbl"><span className="dot" />Built on compliance · Backed by Advani Holdings</span>
            <blockquote data-reveal>
              &ldquo;Licensed import &amp; distribution, authorized brand representation, official Amazon &amp; Noon vendor — <span className="acc">verified in every jurisdiction we trade in.</span>&rdquo;
            </blockquote>
            <div className="trust-meta" data-reveal>
              Optrenex International General Trading LLC — Dubai, UAE · Optrenex International Private Limited — Mumbai, India
            </div>
            <div className="cred-chips" data-reveal>
              <span className="chip">UAE TRADE LIC 1546242</span>
              <span className="chip">MEDIA LIC 0559605</span>
              <span className="chip">VAT TRN 105290096400003</span>
              <span className="chip">CIN U47990MH2025PTC451227</span>
              <span className="chip">LEI 894500WSZ5AA9ZF15679</span>
              <span className="chip">IEC AAECO7780L</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ CTA ═══════════════════════════════════════════ */}
      <section className="cta" id="contact">
        <div className="wrap">
          <h2 className="h-display" data-reveal>The future of gaming<br />distribution <span className="acc">starts today.</span></h2>
          <p data-reveal>Join 203+ retailers and 250+ B2B companies who trust Optrenex. Storage, API, dropshipping, white-labeling — everything you need to scale.</p>
          <div className="hero-ctas" data-reveal>
            <a className="btn btn-accent" href="https://b2b.optrenex.com/register" data-testid="cta-register-btn">Get started</a>
            <Link className="btn btn-line" to="/contact" data-testid="cta-contact-btn">Contact us</Link>
          </div>
          <div className="mail-grid">
            <div className="mail" data-reveal>
              <span className="lbl">Sales</span>
              <a href="mailto:sales@optrenex.com">sales@optrenex.com</a>
            </div>
            <div className="mail" data-reveal>
              <span className="lbl">Sourcing</span>
              <a href="mailto:sourcing@optrenex.com">sourcing@optrenex.com</a>
            </div>
            <div className="mail" data-reveal>
              <span className="lbl">Compliance</span>
              <a href="mailto:compliance@optrenex.com">compliance@optrenex.com</a>
            </div>
            <div className="mail" data-reveal>
              <span className="lbl">Accounts</span>
              <a href="mailto:accounts@optrenex.com">accounts@optrenex.com</a>
            </div>
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════ FOOTER ═══════════════════════════════════════════ */}
      <footer className="bg-bg2 border-t border-line relative overflow-hidden" style={{ padding: 'calc(var(--sp)*1.6) var(--sp) 26px' }}>
        <div className="foot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--sp)', position: 'relative', zIndex: 2 }}>
          <div className="foot-brand">
            <a className="nav-logo flex items-center gap-2" href="#top">
              <span className="mark w-[26px] h-[26px] rounded-[7px] bg-gradient-to-br from-cyan to-cyan-deep grid place-items-center text-[#03222d] text-[15px]">O</span>
              <span className="font-heading text-[19px] tracking-[0.06em] uppercase">Optrenex</span>
            </a>
            <p className="text-dim text-[13.5px] max-w-[330px] mt-[14px]">
              Global gaming distribution powerhouse headquartered in Dubai, UAE. Backed by Advani Holdings.
            </p>
          </div>
          <div>
            <h5 className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-dim mb-[18px]">Company</h5>
            <ul className="list-none">
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="#services" className="hover:text-cyan">Services</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="#portfolio" className="hover:text-cyan">Portfolio</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="#network" className="hover:text-cyan">Network</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="#credentials" className="hover:text-cyan">Credentials</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-dim mb-[18px]">Trade</h5>
            <ul className="list-none">
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="https://b2b.optrenex.com/register" className="hover:text-cyan">Become a customer</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="https://b2b.optrenex.com/register" className="hover:text-cyan">Become a supplier</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="https://b2b.optrenex.com/login" className="hover:text-cyan">B2B login</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="https://retail.optrenex.com" className="hover:text-cyan">Browse retail</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-dim mb-[18px]">Reach us</h5>
            <ul className="list-none">
              <li className="mb-[10px] text-[14px] text-[#c3ced6]">HQ: Office 120 &amp; 121, Al Salam Building, Al Buteen, Murshid Bazar, Al Sabkha, Deira, Dubai, UAE — PO Box 1540</li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]">Mumbai: Om Palace CHS, Khar, Mumbai 400052</li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]"><a href="mailto:sales@optrenex.com" className="hover:text-cyan">sales@optrenex.com</a></li>
              <li className="mb-[10px] text-[14px] text-[#c3ced6]" data-testid="footer-landline"><a href="tel:+97145723353" className="hover:text-cyan">+971-45723353</a></li>
            </ul>
          </div>
        </div>
        
        <div className="watermark font-heading text-[clamp(90px,17vw,300px)] leading-[0.8] uppercase tracking-[0.02em] text-transparent text-center mt-[60px] select-none" style={{ WebkitTextStroke: '1px rgba(233,241,246,.12)' }}>
          Optrenex
        </div>
        
        <div className="foot-legal flex justify-between gap-[20px] flex-wrap border-t border-line mt-[40px] pt-[22px] font-mono text-[10px] tracking-[0.14em] text-dim uppercase">
          <span>© 2026 Optrenex International — All rights reserved</span>
          <span>Media license 0559605 · Importing &amp; distributing video games</span>
        </div>
      </footer>
    </main>
  );
}
